import { useEffect, useRef } from 'react';

import { cn } from '@/lib/utils';

import IconAttachment from '~/svg/IconCodeFile.svg';
import Button, { ButtonProps } from '@/decodo/components/atoms/Button';

type InputAttributes = React.DetailedHTMLProps<
  React.InputHTMLAttributes<HTMLInputElement>,
  HTMLInputElement
>;
type AcceptedAttributes =
  | 'accept'
  | 'children'
  | 'name'
  | 'ref'
  | 'required'
  | 'onBlur';

export interface FileInputProps
  extends Omit<Pick<InputAttributes, AcceptedAttributes>, 'value'> {
  value: File[];
  onChange: (files: File[]) => void;
  classNames?: {
    root?: string;
    input?: ButtonProps['classNames'];
  };
}

export function FileInput({
  children,
  classNames,
  onChange,
  value,
  ...rest
}: FileInputProps) {
  const inputRef = useRef<HTMLInputElement>(null);

  const handleChange: React.ChangeEventHandler<HTMLInputElement> = (event) => {
    const files = event.target.files;
    if (!files) return onChange([]);
    onChange(Array.from(files));
  };

  useEffect(() => {
    if (inputRef.current) {
      const dataTransfer = new DataTransfer();
      value.forEach((file) => dataTransfer.items.add(file));
      inputRef.current.files = dataTransfer.files;
    }
  }, [value]);

  return (
    <div className={cn('relative', classNames?.root)}>
      <input
        {...rest}
        type='file'
        ref={inputRef}
        className='peer sr-only'
        onChange={handleChange}
      />
      <Button
        type='button'
        classNames={{
          ...classNames?.input,
          root: cn('peer-focus:border-2', classNames?.input?.root),
        }}
        variant='outlined'
        leftSection={<IconAttachment className='mr-1' />}
        aria-hidden
        fullwidth
        tabIndex={-1}
        onClick={() => inputRef.current?.click()}
      >
        {children}
      </Button>
    </div>
  );
}
